import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { useEffect, useEffectEvent } from "react";

import type { LongFormAiEventPayload } from "../events";

import { sendSalesErrorMetric } from "../../../shared-utils";
import { LONG_FORM_AI_EVENT } from "../events";
import { useLongFormAiStore } from "../store/useLongFormAiStore";

const logger = getLogger({ module: "useLongFormAiListener" });

/**
 * Bus payloads are `any` and `TinyEmitter.on` does not wrap handlers, so an unchecked
 * `payload.narrative.steps` would throw inside a native listener — no log, no metric, narrative gone.
 * Shallow on purpose: the real contract gate is the zod schema in `expert-workspace-api`
 * `packages/shared-types/long-form-ai.ts`.
 */
const isLongFormAiEventPayload = (payload: unknown): payload is LongFormAiEventPayload => {
    if (typeof payload !== "object" || payload === null) return false;
    const { sessionId, sku, narrative } = payload as Partial<LongFormAiEventPayload>;
    return (
        typeof sessionId === "string" &&
        sessionId.length > 0 &&
        typeof sku === "string" &&
        sku.length > 0 &&
        typeof narrative === "object" &&
        narrative !== null &&
        Array.isArray(narrative.steps)
    );
};

/** Resolves the app sessionId from the producer's contactId, which differ on callback/conference. */
export type ResolveSessionId = (contactId: string) => string;

/**
 * Whether guided browsing is active for this SKU. Injected because the real predicate is
 * `features.isGuidedBrowsingEnabled && !!productInfo?.guidedBrowsing`, which needs workspace features
 * and the session's product info — neither in scope where the DSO listener registers. `sessionId` is
 * the RESOLVED app session id, so a narrative for a backgrounded session is keyed to that session and
 * not the focused one. The LANGUAGE it is looked up in is not: the wrapper reads one app-wide selected
 * language, so a backgrounded session in another language resolves against the on-screen one.
 */
export type IsGuidedBrowsing = (sku: string, sessionId: string) => boolean;

const identity: ResolveSessionId = (id) => id;
const guidedBrowsingOff: IsGuidedBrowsing = () => false;

/**
 * Inbound Long Form AI narrative listener. Registered unconditionally with only rendering gated,
 * matching `useDynamicSmartOfferListener`, so a narrative arriving before the surface mounts is kept.
 *
 * Mounted in expert-ui via `useLongFormAiListenerWrapper`, which supplies both injectables.
 */
export function useLongFormAiListener(
    resolveSessionId: ResolveSessionId = identity,
    isGuidedBrowsing: IsGuidedBrowsing = guidedBrowsingOff,
) {
    const resolve = useEffectEvent(resolveSessionId);
    const resolveGuidedBrowsing = useEffectEvent(isGuidedBrowsing);

    useEffect(() => {
        return expertWorkspaceWebSocketEventBus.on(`ws_${LONG_FORM_AI_EVENT}`, (payload: unknown) => {
            if (!isLongFormAiEventPayload(payload)) {
                // warn, not error: both consumers log this class at warn on purpose — the expert cannot
                // act on a bad producer payload, and the metric is what should drive follow-up.
                logger.warn({ payloadType: typeof payload }, "[LFA] discarded malformed narrative event");
                void sendSalesErrorMetric("LongFormAiMalformedEvent");
                return;
            }

            const sessionId = resolve(payload.sessionId);
            const guidedBrowsing = resolveGuidedBrowsing(payload.sku, sessionId);
            useLongFormAiStore.getState().receiveNarrativeEvent({ ...payload, sessionId }, guidedBrowsing);

            // Logged after the write so `stepCount` is what the expert sees — the store's
            // guided-browsing filter always drops one step. Scalars only, because the narrative adds
            // bulk without adding signal, not because it is sensitive (it is copy read to the
            // customer, and the bus logs the full payload on every emit anyway).
            const stored = useLongFormAiStore.getState().getSession(sessionId);
            logger.info(
                {
                    resolvedSessionId: sessionId,
                    contactId: payload.sessionId,
                    sku: payload.sku,
                    guidedBrowsing,
                    producerStepCount: payload.narrative.steps.length,
                    stepCount: stored.narrative?.steps.length ?? 0,
                },
                "Long form AI narrative event received",
            );
        });
    }, []);
}
